"use client";

import { useIsMobile } from "@/features/layout/useIsMobile";
import { cn } from "@/lib/cn";
import { useItems, useItemsInfos } from "../hooks/useItems";
import { usePage } from "../hooks/usePage";
import { useListViewType } from "../useListViewType";
import { Pagination } from "./Pagination";
import { ResultHeader } from "./ResultHeader";
import { ResultList } from "./ResultList";

/**
 * The whole results panel: header, scrolling list, pagination.
 *
 * The three-row grid is what keeps the list scrolling rather than the panel:
 * header and pagination size to their content, the list takes what's left, and
 * `min-h-0` lets it actually shrink — without it the grid row grows to fit its
 * contents and the scroll container never overflows.
 */
export function ResultsPanel() {
  const isMobile = useIsMobile();
  const { viewType } = useListViewType();
  const { isPending } = useItems();
  const { count, pageCount } = useItemsInfos();
  const [page, setPage] = usePage();

  const isCards = viewType === "cards" && !isMobile;

  return (
    <div
      className={cn(
        "results relative mt-4 min-h-0 flex-grow dark:bg-dark-color",
        // Cards sit on a tinted ground so the white cards read as cards.
        isCards ? "bg-[#F2F2F2]" : "bg-white"
      )}
    >
      <div className="grid h-full min-h-0 grid-rows-[auto_1fr_auto]">
        <ResultHeader />
        <ResultList />

        {count > 0 && (
          <div
            className={cn(
              "grid max-h-[62px] shrink-0 grid-rows-1 bg-white pb-5 pl-5 pt-2.5",
              isMobile ? "pr-5" : "pr-2",
              "dark:bg-dark-color"
            )}
          >
            {/* Hidden while loading rather than showing a stale page count that
                is about to change under the user. */}
            {!isPending && (
              <Pagination
                currentPage={page}
                totalPages={pageCount}
                setPage={(next) => void setPage(next === 1 ? null : next)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
